import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartPulse, Crown, Sparkles } from "lucide-react";
import { API, useAuth } from "../App";
import TrackPreviewPlayer from "./TrackPreviewPlayer";

// Mantra Suite — Healing Mantra + Trillionaire Mantra. Per-user opt-in flags
// (GET/PUT /api/me/mantra). When a mantra is ON, its full-length track is
// playable right here via TrackPreviewPlayer in full-length mode.
const MANTRAS = [
  { key: "healing", flag: "healing_mantra_enabled", icon: HeartPulse, accent: "#3DDC97",
    desc: "A guided calm to breathe, release and restore — play it before you create, perform or sleep." },
  { key: "trillionaire", flag: "trillionaire_mantra_enabled", icon: Crown, accent: "#F5C542",
    desc: "An abundance power-up for artists building an empire — play it before every big decision." },
];

const MantraSettings = ({ token, compact = false, showTrillionaire = true }) => {
  const { isArtist, user } = useAuth();
  const trillionaireVisible = showTrillionaire && (isArtist || !!user?.is_admin);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/me/mantra`, { headers })
      .then((r) => setSettings(r.data))
      .catch(() => setSettings({ healing_mantra_enabled: false, trillionaire_mantra_enabled: false, tracks: {} }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggle = async (flag) => {
    if (!settings || saving) return;
    const next = !settings[flag];
    setSaving(flag);
    try {
      const r = await axios.put(`${API}/me/mantra`, { [flag]: next }, { headers });
      setSettings(r.data);
      toast.success(next ? "Mantra on — press play whenever you need it." : "Mantra off.");
    } catch (e) {
      toast.error("Couldn't save your mantra setting");
    } finally {
      setSaving(null);
    }
  };

  if (!settings) return null;
  const visibleMantras = MANTRAS.filter((m) => trillionaireVisible || m.key !== "trillionaire");

  return (
    <div className={compact ? "space-y-4" : "space-y-6"} data-testid="mantra-settings">
      <div>
        <h2 className={`font-['Unbounded'] ${compact ? "text-lg" : "text-xl"} font-bold text-white mb-1 flex items-center gap-2`}>
          <Sparkles className="w-4 h-4 text-[#EC008C]" /> Mantra Suite
        </h2>
        <p className="text-[#8A92A0] text-sm" data-testid="mantra-description">
          {trillionaireVisible
            ? "Two daily mantras, recorded for StardomFans artists and fans. Switch one on to unlock its player."
            : "A daily mantra, recorded for StardomFans artists and fans."}
        </p>
      </div>
      <div className={`grid grid-cols-1 ${trillionaireVisible ? "lg:grid-cols-2" : ""} gap-4`}>
        {visibleMantras.map((m) => {
          const on = !!settings[m.flag];
          const track = settings.tracks?.[m.key];
          const Icon = m.icon;
          return (
            <div key={m.key} className="card p-5 space-y-4" data-testid={`mantra-card-${m.key}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center border" style={{ background: `${m.accent}14`, borderColor: `${m.accent}40` }}>
                    <Icon className="w-5 h-5" style={{ color: m.accent }} />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{track?.title || `${m.key} mantra`}</p>
                    <p className="text-xs text-[#6A7280] mt-0.5">{m.desc}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggle(m.flag)}
                  disabled={saving === m.flag}
                  role="switch"
                  aria-checked={on}
                  aria-label={`Toggle ${track?.title || m.key}`}
                  data-testid={`toggle-${m.key}-mantra`}
                  className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ${on ? "bg-[#EC008C]" : "bg-[#1A2030]"}`}
                >
                  <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform absolute top-0.5 ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </button>
              </div>
              {on && track?.url && (
                <TrackPreviewPlayer
                  previewUrl={`${window.location.origin}${track.url}`}
                  trackName={track.subtitle || track.title}
                  kicker={`${track.title} · ${track.duration_seconds}s`}
                  limitSeconds={(track.duration_seconds || 60) + 2}
                  hideCta
                  icon={Icon}
                  testId={`mantra-player-${m.key}`}
                  playTestId={`mantra-play-${m.key}`}
                  seekTestId={`mantra-seek-${m.key}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MantraSettings;
